/**
 * CpaWorkpaperTextarea — Phase 10 Task 10.5
 *
 * Wraps a <textarea> with the CpaPrivilegeWarning banner. Use this in
 * every CPA-facing form where the witness accountant authors free-text
 * notes (approve, dispute, waive, override, sign-off, transaction notes).
 *
 * The wrapper exists so the manifest grep contract is one-liner:
 *   "every entry in cpa_warned_surfaces.frontend_component imports
 *    CpaWorkpaperTextarea (or CpaPrivilegeWarning directly)".
 *
 * Pass-through API surface mirrors a normal textarea + an explicit label.
 * Caller still owns value/onChange/validation — this component is
 * unopinionated about content rules.
 */

import React from 'react';
import CpaPrivilegeWarning from './CpaPrivilegeWarning';

export interface CpaWorkpaperTextareaProps
  extends React.DetailedHTMLProps<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  > {
  /** Visible label text. Required (CPA workpaper inputs must be labeled). */
  label: React.ReactNode;
  /** Optional helper text rendered below the textarea. */
  helperText?: React.ReactNode;
  /** Wrapper div className override (rare — most callers won't need this). */
  containerClassName?: string;
  /** Test id for the banner (defaults to id-suffixed if id provided). */
  bannerTestId?: string;
  /**
   * Render the privilege banner (default true). Set false to DEDUPE when the same
   * modal already shows exactly one CpaPrivilegeWarning elsewhere — a second banner
   * for a second textarea in the same surface is noise, and the manifest/e2e contract
   * only needs one banner region per surface.
   */
  showBanner?: boolean;
}

export default function CpaWorkpaperTextarea({
  label,
  helperText,
  containerClassName,
  bannerTestId,
  showBanner = true,
  id,
  className,
  ...textareaProps
}: CpaWorkpaperTextareaProps) {
  const resolvedBannerTestId =
    bannerTestId ?? (id ? `${id}-privilege-warning` : 'cpa-privilege-warning');
  return (
    <div className={containerClassName}>
      {showBanner && <CpaPrivilegeWarning testId={resolvedBannerTestId} />}
      {label != null && (
        <label
          htmlFor={id}
          className="block text-sm font-medium mb-1"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {label}
        </label>
      )}
      <textarea
        id={id}
        className={
          className ??
          'w-full px-3 py-2 border border-[color:var(--color-border)] rounded-lg text-sm focus:ring-2 focus:ring-[color:var(--color-brand)] focus:border-[color:var(--color-brand)]'
        }
        {...textareaProps}
      />
      {helperText != null && (
        <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>{helperText}</p>
      )}
    </div>
  );
}

export { CpaWorkpaperTextarea };
